/**
 * Reparto de bolas lógicas entre bolas visuales (lógica pura, sin Phaser).
 *
 * El tablero solo puede mostrar como máximo `MAX_VISUAL_BALLS` cuerpos
 * físicos a la vez, pero un vertido puede traer muchísimas más bolas que
 * eso (p.ej. 500). La solución: cada bola visual "porta" un peso entero, y
 * la suma de todos los pesos es EXACTAMENTE el total de bolas del vertido
 * — sin perder ni inventar bolas por redondeo. El contador lógico sigue
 * siendo la fuente de verdad; el render es solo una representación
 * proporcional de densidad.
 */

import { MAX_VISUAL_BALLS } from './constants';

/**
 * Devuelve el peso (bolas lógicas que representa) de cada bola visual que
 * hay que animar para un vertido de `totalBalls` bolas, respetando el tope
 * `maxVisual`. La suma de los pesos devueltos siempre es igual a
 * `Math.floor(totalBalls)` (o 0 si `totalBalls <= 0`).
 */
export function distributeBallWeights(
  totalBalls: number,
  maxVisual: number = MAX_VISUAL_BALLS
): number[] {
  const total = Math.max(0, Math.floor(totalBalls));
  if (total <= 0) return [];

  const cap = Math.max(1, Math.floor(maxVisual));
  const visualCount = Math.min(total, cap);
  const base = Math.floor(total / visualCount);
  const remainder = total - base * visualCount;

  // Las primeras `remainder` bolas visuales cargan una unidad extra, así la
  // suma total cuadra exacto sin necesitar pesos fraccionarios.
  return Array.from({ length: visualCount }, (_, i) => base + (i < remainder ? 1 : 0));
}
